/* eslint-disable react-hooks/refs, react-hooks/immutability, react-hooks/set-state-in-effect --
   This component bridges React to an imperative editor engine (`angularEditor/engine.ts`, a port of the Angular
   component's mutable state: the toolbar renders fields the engine updates from DOM events, and the ae-select applies
   NgModel's write rules in an effect), which is the pattern these rules exist to discourage. */
import {
  createElement,
  forwardRef,
  useEffect,
  useLayoutEffect,
  useReducer,
  useRef,
  useState,
  type HTMLAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import type { RichTextConfig } from '@/core/models/richText';
import { AngularEditorEngine, isButtonHidden } from './angularEditor/engine';
import './RichTextEditor.scss';
import './FieldStatus.scss';

interface RichTextEditorProps {
  /** HTML (the `ngModel`). */
  value: string | null | undefined;
  /** Called with the sanitised HTML on every user change, like the `ngModelChange` of `<angular-editor>`. */
  onChange?: (html: string) => void;
  config?: RichTextConfig;
  className?: string;
  /** The `id` attribute of `<angular-editor id="...">`; toolbar button ids are `<name>-<id>`. */
  id?: string;
}

interface PickerOption {
  label: string;
  value: string;
}

/**
 * Chrome's execCommand copies the *specified* value of a matched rule into the markup it writes (a span gets
 * `background-color: transparent;` when the editor's rule says `transparent`). The production CSS minifier rewrites
 * that keyword to `#0000`, which would save `rgba(0, 0, 0, 0)` instead, so this one declaration is added at run time
 * where no minifier touches it. (Angular's own build keeps the keyword.)
 */
const KEYWORD_RULE =
  '.angular-editor.angular-editor .angular-editor-wrapper .angular-editor-textarea{background-color:transparent}';
function ensureKeywordRule(): void {
  if (document.getElementById('angular-editor-keywords')) return;
  const style = document.createElement('style');
  style.id = 'angular-editor-keywords';
  style.textContent = KEYWORD_RULE;
  document.head.appendChild(style);
}

/** A custom element host (JSX has no typing for `ae-select`, `angular-editor`, ...). */
function customHost(tag: string) {
  return forwardRef<HTMLElement, HTMLAttributes<HTMLElement> & { [attribute: string]: unknown }>(
    function CustomHost(props, ref) {
      return createElement(tag, { ...props, ref });
    },
  );
}
const AngularEditorHost = customHost('angular-editor');
const AngularEditorToolbarHost = customHost('angular-editor-toolbar');
const AeSelectHost = customHost('ae-select');

const HEADINGS: PickerOption[] = [
  { label: 'Heading 1', value: 'h1' },
  { label: 'Heading 2', value: 'h2' },
  { label: 'Heading 3', value: 'h3' },
  { label: 'Heading 4', value: 'h4' },
  { label: 'Heading 5', value: 'h5' },
  { label: 'Heading 6', value: 'h6' },
  { label: 'Heading 7', value: 'h7' },
  { label: 'Paragraph', value: 'p' },
  { label: 'Predefined', value: 'pre' },
  { label: 'Standard', value: 'div' },
  { label: 'default', value: 'default' },
];

/** `config.fonts` of angular-editor's default config (no screen overrides it). */
const FONTS: PickerOption[] = ['Arial', 'Times New Roman', 'Calibri', 'Comic Sans MS'].map((name) => ({
  label: name,
  value: name,
}));
const SIZES: PickerOption[] = ['1', '2', '3', '4', '5', '6', '7'].map((size) => ({ label: size, value: size }));

/**
 * The toolbar binds `[fonts]="getFonts()"`, which builds a new array on every change-detection pass, so Angular
 * removes and re-creates the font list's buttons after every event handled in the page (input, keyup, click, timers).
 * Chrome ends the current undo step when the DOM changes between two keystrokes, so in angular-editor Undo (and
 * Ctrl+Z) reverts typing one character at a time. Re-appending the list's buttons on every render of the editor
 * reproduces exactly that mutation (an untouched DOM would undo a whole typed run at once).
 */
function useOptionChurn(list: RefObject<HTMLSpanElement | null>, enabled: boolean): void {
  useLayoutEffect(() => {
    const element = list.current;
    if (enabled && element) element.append(...Array.from(element.children));
  });
}

/**
 * angular-editor's `ae-select`: a button plus a list of buttons, bound like `[(ngModel)]` (the model only reaches the
 * widget when the bound value changes and differs from the value the user last picked, exactly `NgModel`'s rule).
 */
function AeSelect({
  className,
  options,
  model,
  hidden,
  disabled,
  churnOptions = false,
  onModel,
  onChange,
}: {
  className: string;
  options: PickerOption[];
  model: string;
  hidden: boolean;
  disabled: boolean;
  /** See `useOptionChurn`. */
  churnOptions?: boolean;
  /** `ngModelChange`: the picked value. */
  onModel: (value: string) => void;
  /** `(change)`: runs after the model was updated. */
  onChange: () => void;
}) {
  const indexOf = (value: string) => options.findIndex((option) => option.value === value);
  const [selected, setSelected] = useState(() => Math.max(0, indexOf(model)));
  const [optionId, setOptionId] = useState(() => Math.max(0, indexOf(model)));
  const [opened, setOpened] = useState(false);
  const host = useRef<HTMLElement | null>(null);
  const optionsList = useRef<HTMLSpanElement | null>(null);
  useOptionChurn(optionsList, churnOptions);
  const viewModel = useRef<string | undefined>(undefined);
  const first = useRef(true);

  // NgModel.ngOnChanges: write the bound value into the widget unless it is what the user just picked
  useEffect(() => {
    if (first.current || !Object.is(viewModel.current, model)) {
      first.current = false;
      viewModel.current = model;
      // AeSelectComponent.writeValue: empty / non-string values are ignored, unknown values keep the selection
      if (model && typeof model === 'string') {
        const index = indexOf(model);
        if (index >= 0) {
          setSelected(index);
          setOptionId(index);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model]);

  // @HostListener('document:click')
  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!host.current?.contains(event.target as Node)) setOpened(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  const optionSelect = (option: PickerOption, event: { stopPropagation: () => void }) => {
    event.stopPropagation();
    const index = indexOf(option.value);
    if (index >= 0) {
      setSelected(index);
      setOptionId(index);
    }
    viewModel.current = option.value;
    onModel(option.value);
    onChange();
    setOpened(false);
  };

  const selectedOption = options[selected];
  const label = selectedOption && Object.hasOwn(selectedOption, 'label') ? selectedOption.label : 'Select';

  return (
    <AeSelectHost
      className={className}
      tabIndex={-1}
      style={{ display: hidden ? 'none' : 'inline-block' }}
      ref={host}
      onKeyDown={(event: ReactKeyboardEvent) => {
        if (!opened) return;
        switch (event.key) {
          case 'ArrowDown':
            if (optionId < options.length - 1) setOptionId(optionId + 1);
            break;
          case 'ArrowUp':
            if (optionId >= 1) setOptionId(optionId - 1);
            break;
          case 'Enter':
            optionSelect(options[optionId], event);
            break;
          case 'Escape':
            setOpened(false);
            event.preventDefault();
            break;
        }
      }}
    >
      <span className={`ae-font ae-picker${opened ? ' ae-expanded' : ''}`}>
        <button
          tabIndex={-1}
          type="button"
          role="button"
          className={`ae-picker-label${disabled ? ' disabled' : ''}`}
          disabled={disabled}
          onClick={() => {
            if (disabled) return;
            setOpened((current) => !current);
          }}
        >
          {label}
          <svg viewBox="0 0 18 18">
            <polygon className="ae-stroke" points="7 11 9 13 11 11 7 11"></polygon>
            <polygon className="ae-stroke" points="7 7 9 5 11 7 7 7"></polygon>
          </svg>
        </button>
        <span className="ae-picker-options" ref={optionsList}>
          {options.map((option, i) => (
            <button
              key={option.value}
              tabIndex={-1}
              type="button"
              role="button"
              className={`ae-picker-item${option.value === selectedOption?.value ? ' selected' : ''}${
                i === optionId ? ' focused' : ''
              }`}
              onClick={(event) => optionSelect(option, event)}
            >
              {option.label}
            </button>
          ))}
          {options.length === 0 && <span className="dropdown-item">No items for select</span>}
        </span>
      </span>
    </AeSelectHost>
  );
}

/** A hidden `<input>` whose `(change)` handler is the native `change` event (React's `onChange` is `input`). */
function HiddenInput({
  type,
  accept,
  onNativeChange,
  inputRef,
}: {
  type: 'color' | 'file';
  accept?: string;
  onNativeChange: (input: HTMLInputElement) => void;
  inputRef: RefObject<HTMLInputElement | null>;
}) {
  const handler = useRef(onNativeChange);
  useLayoutEffect(() => {
    handler.current = onNativeChange;
  });
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return undefined;
    const listener = () => handler.current(input);
    input.addEventListener('change', listener);
    return () => input.removeEventListener('change', listener);
  }, [inputRef]);
  return <input ref={inputRef} style={{ display: 'none' }} accept={accept} type={type} />;
}

/**
 * `@kolkov/angular-editor` 2.1.0 for React: same DOM, classes, toolbar (ids, order, hidden buttons), and the same
 * editing behaviour, because the logic is a port of the original (`./angularEditor/engine.ts`): a `contenteditable`
 * driven by `document.execCommand`, sanitised with Angular's own algorithm before the value is reported. Saved HTML is
 * therefore what the Angular editor saved for the same actions (see PARITY_DECISIONS D10 and the parity script in
 * `tools/migration/rte-parity`).
 */
export function RichTextEditor({
  value,
  onChange,
  config = { editable: true },
  className = '',
  id = '',
}: RichTextEditorProps) {
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const configRef = useRef(config);
  const onChangeRef = useRef(onChange);
  const viewModel = useRef<string | null | undefined | symbol>(Symbol('unset'));
  const host = useRef<HTMLElement | null>(null);
  const textArea = useRef<HTMLDivElement>(null);
  const wrapper = useRef<HTMLDivElement>(null);
  const foreground = useRef<HTMLInputElement>(null);
  const background = useRef<HTMLInputElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  useLayoutEffect(() => {
    configRef.current = config;
    onChangeRef.current = onChange;
  });

  const [engine] = useState(
    () =>
      new AngularEditorEngine({
        getConfig: () => configRef.current,
        id,
        notify: () => rerender(),
        onChange: (html) => {
          // NgModel.viewToModelUpdate: the emitted value is the view model, so the echo of it is not written back
          viewModel.current = html;
          onChangeRef.current?.(html);
        },
      }),
  );

  // wiring of the template's event bindings (native listeners: `focus`/`blur` do not bubble, `change` is not `input`)
  useLayoutEffect(() => {
    const area = textArea.current;
    const hostElement = host.current;
    if (!area || !hostElement) return undefined;
    ensureKeywordRule();
    engine.textArea = area;
    engine.wrapper = wrapper.current as HTMLElement;
    const onInput = (event: Event) => engine.onContentChange(event.target as HTMLElement);
    const onClick = () => {
      engine.exec();
      rerender();
    };
    hostElement.addEventListener('focus', engine.onHostFocus);
    area.addEventListener('input', onInput);
    area.addEventListener('focus', engine.onTextAreaFocus);
    area.addEventListener('blur', engine.onTextAreaBlur as EventListener);
    area.addEventListener('click', onClick);
    area.addEventListener('keyup', onClick);
    area.addEventListener('mouseout', engine.onTextAreaMouseOut);
    return () => {
      hostElement.removeEventListener('focus', engine.onHostFocus);
      area.removeEventListener('input', onInput);
      area.removeEventListener('focus', engine.onTextAreaFocus);
      area.removeEventListener('blur', engine.onTextAreaBlur as EventListener);
      area.removeEventListener('click', onClick);
      area.removeEventListener('keyup', onClick);
      area.removeEventListener('mouseout', engine.onTextAreaMouseOut);
    };
  }, [engine]);

  useEffect(() => () => engine.dispose(), [engine]);

  // ngModel -> writeValue, only when the bound value differs from the last value the editor itself reported
  useLayoutEffect(() => {
    if (!Object.is(viewModel.current, value)) {
      viewModel.current = value;
      engine.writeValue(value);
    }
  }, [engine, value]);

  const position = config.toolbarPosition || 'top';
  const showToolbar = config.showToolbar !== undefined ? config.showToolbar : true;
  const hiddenButtons = config.toolbarHiddenButtons;
  const hide = (name: string) => isButtonHidden(hiddenButtons, name);
  const htmlMode = engine.htmlMode;

  const toolbarButton = (
    name: string | null,
    title: string,
    icon: string,
    command: string,
    options: { disabled?: boolean } = {},
  ): ReactNode => (
    <button
      id={name === null ? undefined : `${name}-${id}`}
      type="button"
      title={title}
      className="angular-editor-button"
      onClick={() => engine.triggerCommand(command)}
      disabled={options.disabled}
      hidden={hide(command)}
      tabIndex={-1}
    >
      <i className={`fa ${icon}`}></i>
    </button>
  );

  const toolbar: ReactNode = showToolbar ? (
    <AngularEditorToolbarHost>
      <div className="angular-editor-toolbar">
        <div className="angular-editor-toolbar-set">
          {toolbarButton(null, 'Undo', 'fa-undo', 'undo')}
          {toolbarButton(null, 'Redo', 'fa-repeat', 'redo')}
        </div>
        <div className="angular-editor-toolbar-set">
          {toolbarButton('bold', 'Bold', 'fa-bold', 'bold', { disabled: htmlMode })}
          {toolbarButton('italic', 'Italic', 'fa-italic', 'italic', { disabled: htmlMode })}
          {toolbarButton('underline', 'Underline', 'fa-underline', 'underline', { disabled: htmlMode })}
          {toolbarButton('strikeThrough', 'Strikethrough', 'fa-strikethrough', 'strikeThrough', { disabled: htmlMode })}
          {toolbarButton('subscript', 'Subscript', 'fa-subscript', 'subscript', { disabled: htmlMode })}
          {toolbarButton('superscript', 'Superscript', 'fa-superscript', 'superscript', { disabled: htmlMode })}
        </div>
        <div className="angular-editor-toolbar-set">
          {toolbarButton('justifyLeft', 'Justify Left', 'fa-align-left', 'justifyLeft', { disabled: htmlMode })}
          {toolbarButton('justifyCenter', 'Justify Center', 'fa-align-center', 'justifyCenter', { disabled: htmlMode })}
          {toolbarButton('justifyRight', 'Justify Right', 'fa-align-right', 'justifyRight', { disabled: htmlMode })}
          {toolbarButton('justifyFull', 'Justify Full', 'fa-align-justify', 'justifyFull', { disabled: htmlMode })}
        </div>
        <div className="angular-editor-toolbar-set">
          {toolbarButton('indent', 'Indent', 'fa-indent', 'indent', { disabled: htmlMode })}
          {toolbarButton('outdent', 'Outdent', 'fa-outdent', 'outdent', { disabled: htmlMode })}
        </div>
        <div className="angular-editor-toolbar-set">
          {toolbarButton('insertUnorderedList', 'Unordered List', 'fa-list-ul', 'insertUnorderedList', {
            disabled: htmlMode,
          })}
          {toolbarButton('insertOrderedList', 'Ordered List', 'fa-list-ol', 'insertOrderedList', {
            disabled: htmlMode,
          })}
        </div>
        <div className="angular-editor-toolbar-set">
          <AeSelect
            className="select-heading"
            options={HEADINGS}
            model={engine.block}
            hidden={hide('heading')}
            disabled={htmlMode}
            onModel={(v) => {
              engine.block = v;
            }}
            onChange={() => engine.triggerCommand(engine.block)}
          />
        </div>
        <div className="angular-editor-toolbar-set">
          <AeSelect
            className="select-font"
            options={FONTS}
            model={engine.fontName}
            hidden={hide('fontName')}
            disabled={htmlMode}
            churnOptions
            onModel={(v) => {
              engine.fontName = v;
            }}
            onChange={() => engine.setFontName(engine.fontName)}
          />
        </div>
        <div className="angular-editor-toolbar-set">
          <AeSelect
            className="select-font-size"
            options={SIZES}
            model={engine.fontSize}
            hidden={hide('fontSize')}
            disabled={htmlMode}
            onModel={(v) => {
              engine.fontSize = v;
            }}
            onChange={() => engine.setFontSize(engine.fontSize)}
          />
        </div>
        <div className="angular-editor-toolbar-set">
          <HiddenInput
            type="color"
            inputRef={foreground}
            onNativeChange={(input) => engine.insertColor(input.value, 'textColor')}
          />
          <button
            id={`foregroundColorPicker-${id}`}
            type="button"
            className="angular-editor-button"
            onClick={() => {
              engine.executeCommand('focus');
              foreground.current?.click();
            }}
            title="Text Color"
            disabled={htmlMode}
            hidden={hide('textColor')}
            tabIndex={-1}
          >
            <span className="color-label foreground">
              <i className="fa fa-font"></i>
            </span>
          </button>
          <HiddenInput
            type="color"
            inputRef={background}
            onNativeChange={(input) => engine.insertColor(input.value, 'backgroundColor')}
          />
          <button
            id={`backgroundColorPicker-${id}`}
            type="button"
            className="angular-editor-button"
            onClick={() => {
              engine.executeCommand('focus');
              background.current?.click();
            }}
            title="Background Color"
            disabled={htmlMode}
            hidden={hide('backgroundColor')}
            tabIndex={-1}
          >
            <span className="color-label background">
              <i className="fa fa-font"></i>
            </span>
          </button>
        </div>
        <div className="angular-editor-toolbar-set">
          <button
            id={`link-${id}`}
            type="button"
            className="angular-editor-button"
            onClick={() => engine.insertUrl()}
            title="Insert Link"
            disabled={engine.isLinkButtonDisabled}
            hidden={hide('link')}
            tabIndex={-1}
          >
            <i className="fa fa-link"></i>
          </button>
          <button
            id={`unlink-${id}`}
            type="button"
            className="angular-editor-button"
            onClick={() => engine.triggerCommand('unlink')}
            title="Unlink"
            disabled={htmlMode || !engine.linkSelected}
            hidden={hide('unlink')}
            tabIndex={-1}
          >
            <i className="fa fa-chain-broken"></i>
          </button>
          <HiddenInput
            type="file"
            accept="image/*"
            inputRef={fileInput}
            onNativeChange={(input) => engine.onFileChanged(input)}
          />
          <button
            id={`insertImage-${id}`}
            type="button"
            className="angular-editor-button"
            onClick={() => {
              engine.executeCommand('focus');
              fileInput.current?.click();
            }}
            title="Insert Image"
            disabled={htmlMode}
            hidden={hide('insertImage')}
            tabIndex={-1}
          >
            <i className="fa fa-image"></i>
          </button>
          <button
            id={`insertVideo-${id}`}
            type="button"
            className="angular-editor-button"
            onClick={() => engine.insertVideo()}
            title="Insert Video"
            disabled={htmlMode}
            hidden={hide('insertVideo')}
            tabIndex={-1}
          >
            <i className="fa fa-video-camera"></i>
          </button>
          {toolbarButton('insertHorizontalRule', 'Horizontal Line', 'fa-minus', 'insertHorizontalRule', {
            disabled: htmlMode,
          })}
        </div>
        <div className="angular-editor-toolbar-set">
          <button
            id={`clearFormatting-${id}`}
            type="button"
            title="Clear Formatting"
            className="angular-editor-button"
            onClick={() => engine.triggerCommand('removeFormat')}
            disabled={htmlMode}
            hidden={hide('removeFormat')}
            tabIndex={-1}
          >
            <i className="fa fa-remove"></i>
          </button>
        </div>
        <div className="angular-editor-toolbar-set">
          <button
            id={`toggleEditorMode-${id}`}
            type="button"
            title="HTML Code"
            className="angular-editor-button"
            onClick={() => engine.triggerCommand('toggleEditorMode')}
            hidden={hide('toggleEditorMode')}
            tabIndex={-1}
          >
            <i className="fa fa-code"></i>
          </button>
        </div>
      </div>
    </AngularEditorToolbarHost>
  ) : (
    <AngularEditorToolbarHost />
  );

  // angular-editor applies these config values as inline styles verbatim (a bare number such as '160' is not a valid
  // CSS length, so the browser ignores it exactly as it does in Angular).
  const areaStyle = { height: config.height, minHeight: config.minHeight, maxHeight: config.maxHeight };

  // `<angular-editor>` is the host element of the component; the app's global rules select it by tag name.
  return (
    <AngularEditorHost id={id || undefined} tabIndex={-1} ref={host}>
      <div className={`angular-editor ${className}`.trim()} style={{ width: config.width, minWidth: config.minWidth }}>
        {position === 'top' && toolbar}
        <div className="angular-editor-wrapper" ref={wrapper}>
          <div
            ref={textArea}
            className="angular-editor-textarea"
            contentEditable={config.editable}
            suppressContentEditableWarning
            translate={config.translate as 'yes' | 'no' | undefined}
            spellCheck={config.spellcheck}
            style={areaStyle}
          ></div>
          <span className="angular-editor-placeholder">{config.placeholder}</span>
        </div>
        {position === 'bottom' && toolbar}
      </div>
    </AngularEditorHost>
  );
}
